import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { useIntl, defineMessages, IntlShape, FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { ArrowRightIcon } from '@10xLabs/react-web/components/Icons/ArrowRight'
import { Checkbox } from 'components/v2/Checkbox'
import { Column } from '@10xLabs/react-web/components/Column'
import { Row } from '@10xLabs/react-web/components/Row'
import { SelectTags } from 'components/v2/SelectTags'
import { IToggleIsReturnProps, ISelectSegmentsProps, ISegmentDestination, IFormSegment } from './types'

const StyledArrowRightIcon = styled(ArrowRightIcon)<TSFixMe>`
  fill: #5d5c70;
  width: 24px;
  height: 14px;
`
const ArrowLeftIcon = styled(StyledArrowRightIcon)<TSFixMe>`
  transform: rotate(180deg);
`

const Pointer = styled.div<TSFixMe>`
  cursor: pointer;
`
const SplitColumn = styled(Column)<TSFixMe>`
  flex: 0.1;
  align-self: center;
  text-align: center;
`

const CheckboxContainer = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
  margin: 5px 0 -15px;
  gap: 8px;
`

export const BothWay = (): ReactElement => {
  return (
    <div>
      <StyledArrowRightIcon />
      <ArrowLeftIcon />
    </div>
  )
}

export const OneWay = (): ReactElement => {
  return (
    <div>
      <StyledArrowRightIcon />
    </div>
  )
}

export const ToggleIsReturn = (props: IToggleIsReturnProps): ReactElement => {
  return (
    <Pointer
      onClick={(): void => {
        if (props.disabled) {
          return
        }
        props.setFieldValue('isReturn', !props.values.isReturn)
        props.setFieldValue('allSegmentsAreSelected', false)
        props.setFieldValue('segments', [])
        props.setFieldValue('segmentsOrigins', [])
        props.setFieldValue('segmentsDestinations', [])
        props.setFieldValue('allServicesAreSelected', false)
        props.setFieldValue('routeServices', [])
      }}
    >
      {props.children}
    </Pointer>
  )
}
const messages = defineMessages({
  selectSegmentsOrigins: {
    id: 'fareRules.form.segmentsOrigins.placeholder',
    defaultMessage: 'Select origins'
  },
  selectSegmentsDestinations: {
    id: 'fareRules.form.segmentsDestinations.placeholder',
    defaultMessage: 'Select destinations'
  },
  allOriginsOption: {
    id: 'fareRules.form.segmentsOrigins.all',
    defaultMessage: 'All origins'
  },
  allDestinationsOption: {
    id: 'fareRules.form.segmentsDestinations.all',
    defaultMessage: 'All destinations'
  }
})

const getFilteredPlaces = (values: ISegmentDestination[], list: ISegmentDestination[]): ISegmentDestination[] => {
  return list.filter((place): TSFixMe => {
    return isEmpty(values.find(({ id }): TSFixMe => id === place.id))
  })
}

const getFilteredSegmentsOrigins = (
  values: ISegmentDestination[],
  list: ISegmentDestination[],
  intl: IntlShape
): ISegmentDestination[] => {
  const filteredList = getFilteredPlaces(values, list)
  if (!isEmpty(list)) {
    return [
      {
        id: '',
        code: intl.formatMessage(messages.allOriginsOption),
        name: intl.formatMessage(messages.allOriginsOption)
      },
      ...filteredList
    ]
  }

  return filteredList
}

const getFilteredSegmentsDestinations = (
  values: ISegmentDestination[],
  list: ISegmentDestination[],
  intl: IntlShape
): ISegmentDestination[] => {
  const filteredList = getFilteredPlaces(values, list)

  if (!isEmpty(list)) {
    return [
      {
        id: '',
        code: intl.formatMessage(messages.allDestinationsOption),
        name: intl.formatMessage(messages.allDestinationsOption)
      },
      ...filteredList
    ]
  }

  return filteredList
}

export const SelectSegmentsForm = <SegmentValue extends IFormSegment>(
  props: ISelectSegmentsProps<SegmentValue>
): ReactElement => {
  const intl = useIntl()

  const {
    values,
    setFieldValue,
    segments,
    errors,
    touched,
    isReturn,
    segmentsOrigins,
    segmentsDestinations,
    setSegmentsOrigins,
    setSegmentsDestinations,
    setSegments,
    disabled,
    isOnlyAdd,
    isShowingAllSegmentsCheckbox,
    setAllSegmentsAreSelected,
    allSegmentsAreSelected
  } = props

  const getSegmentsSelected = (
    segmentsOrigins: ISegmentDestination[],
    segmentsDestinations: ISegmentDestination[]
  ): SegmentValue[] => {
    return segments.filter((segment): TSFixMe => {
      const isOneWaySegment =
        !isEmpty(segmentsOrigins.find(({ id }): TSFixMe => id === segment.origin.id)) &&
        !isEmpty(segmentsDestinations.find(({ id }): TSFixMe => id === segment.destination.id))

      if (!isReturn) {
        return isOneWaySegment
      }

      const isReturnSegment =
        !isEmpty(segmentsOrigins.find(({ id }): TSFixMe => id === segment.destination.id)) &&
        !isEmpty(segmentsDestinations.find(({ id }): TSFixMe => id === segment.origin.id))

      return isOneWaySegment || isReturnSegment
    })
  }

  const segmentsOriginsList = segments.reduce((result, segment): TSFixMe => {
    if (isEmpty(result.find(({ id }): TSFixMe => id === segment.origin.id))) {
      return [...result, segment.origin]
    }

    return result
  }, [])

  const segmentsDestinationsList = props.segments.reduce((result, segment): TSFixMe => {
    if (isEmpty(result.find(({ id }): TSFixMe => id === segment.destination.id))) {
      return [...result, segment.destination]
    }

    return result
  }, [])

  const handleSegmentsOriginsChange = (newSegmentsOrigins: ISegmentDestination[]): void => {
    const allOption = newSegmentsOrigins.find(({ id }): TSFixMe => !id)

    if (!allOption) {
      setSegmentsOrigins(newSegmentsOrigins)
      setSegments(getSegmentsSelected(newSegmentsOrigins, segmentsDestinations))
    } else {
      setSegmentsOrigins(segmentsOriginsList)
      setSegments(getSegmentsSelected(segmentsOriginsList, segmentsDestinations))
    }
  }

  const handleSegmentsDestinationsChange = (newSegmentsDestinations: ISegmentDestination[]): void => {
    const allOption = newSegmentsDestinations.find(({ id }): TSFixMe => !id)

    if (!allOption) {
      setSegmentsDestinations(newSegmentsDestinations)
      setSegments(getSegmentsSelected(segmentsOrigins, newSegmentsDestinations))
    } else {
      setSegmentsDestinations(segmentsDestinationsList)
      setSegments(getSegmentsSelected(segmentsOrigins, segmentsDestinationsList))
    }
  }

  const handleCheckboxChange = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>): void => {
    if (checked) {
      setAllSegmentsAreSelected && setAllSegmentsAreSelected(true)
      setSegmentsOrigins(segmentsOriginsList)
      setSegmentsDestinations(segmentsDestinationsList)
      setSegments(getSegmentsSelected(segmentsOriginsList, segmentsDestinationsList))
    } else {
      setAllSegmentsAreSelected && setAllSegmentsAreSelected(false)
      setSegmentsOrigins([])
      setSegmentsDestinations([])
      setSegments([])
    }
  }

  return (
    <>
      {isShowingAllSegmentsCheckbox && (
        <Row>
          <Column>
            <CheckboxContainer>
              <Checkbox
                id="loyalty.campaing.rule.new.selectAll.id"
                checked={allSegmentsAreSelected}
                onChange={handleCheckboxChange}
              />
              <FormattedMessage id="inventoryRule.new.applyOnAllSegment.label" defaultMessage="Apply on all segments" />
            </CheckboxContainer>
          </Column>
        </Row>
      )}
      <Row>
        <Column>
          <SelectTags
            id="segmentsOrigins"
            placeholder={intl.formatMessage(messages.selectSegmentsOrigins)}
            values={segmentsOrigins}
            items={getFilteredSegmentsOrigins(segmentsOrigins, segmentsOriginsList, intl)}
            getTagLabel={(place: ISegmentDestination): string => place.code}
            onChange={(values: ISegmentDestination[]): void => handleSegmentsOriginsChange(values)}
            touched={touched.segments}
            errors={errors.segments}
            disabled={disabled}
          />
        </Column>
        <SplitColumn>
          <ToggleIsReturn setFieldValue={setFieldValue} values={values} disabled={isOnlyAdd || disabled}>
            {values.isReturn ? <BothWay /> : <OneWay />}
          </ToggleIsReturn>
        </SplitColumn>
        <Column>
          <SelectTags
            id="segmentsDestinations"
            placeholder={intl.formatMessage(messages.selectSegmentsDestinations)}
            values={segmentsDestinations}
            items={getFilteredSegmentsDestinations(segmentsDestinations, segmentsDestinationsList, intl)}
            getTagLabel={(place: ISegmentDestination): string => place.code}
            onChange={(values: ISegmentDestination[]): void => {
              handleSegmentsDestinationsChange(values)
            }}
            touched={touched.segmentsDestinations}
            errors={errors.segmentsDestinations}
            disabled={disabled}
          />
        </Column>
      </Row>
    </>
  )
}
